import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import { setAccessToken } from './auth/tokenStore'

// Shows the user's name + sign-out when authenticated, or a sign-in button otherwise.
function AuthButton() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <span className="text-gray-400 text-sm">…</span>;
  }

  if (auth.isAuthenticated) {
    const name = auth.user?.profile.given_name || auth.user?.profile.name || auth.user?.profile.email;
    return (
      <div className="flex items-center space-x-3">
        <span className="hidden sm:inline text-gray-600 text-sm">Hi, {name}</span>
        <button
          onClick={() => auth.signoutRedirect()}
          className="text-gray-600 hover:text-orange-600 font-medium transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => auth.signinRedirect()}
      className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-orange-500/25 transition-all"
    >
      Sign in
    </button>
  );
}

// Rendered at /callback while the OIDC redirect is being processed.
function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();

  // Once react-oidc-context has finished the code->token exchange, hand control back
  // to react-router so the SPA leaves /callback and the query string is dropped.
  useEffect(() => {
    if (!auth.isLoading && (auth.isAuthenticated || auth.error)) {
      navigate('/', { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange-500 border-t-transparent mx-auto mb-4" />
        <p className="text-gray-600">{auth.error ? `Sign-in error: ${auth.error.message}` : 'Signing you in…'}</p>
      </div>
    </div>
  );
}

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [triedSilent, setTriedSilent] = useState(false)
  const auth = useAuth()

  // Keep the axios token store in sync with the current session so API calls
  // carry the bearer token. Runs whenever the auth user changes.
  useEffect(() => {
    setAccessToken(auth.user?.access_token ?? null);
  }, [auth.user]);

  // Zero-click SSO: on first load, silently ask the ID server (prompt=none in a hidden
  // iframe) whether there's an existing session. If yes, we sign in with no interaction;
  // if not, the call fails quietly and the user stays logged out (login is optional).
  // Only same-site origins (e.g. *.local.letech.solutions) can pass the cookie here.
  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || triedSilent) return;
    const path = window.location.pathname;
    if (path.startsWith('/callback') || path.startsWith('/silent-renew')) return;
    setTriedSilent(true);
    auth.signinSilent().catch(() => { /* no existing session — stay anonymous */ });
  }, [auth, auth.isLoading, auth.isAuthenticated, triedSilent]);

  return (
    <Router>
      <Routes>
        {/* OIDC sign-in landing. react-oidc-context processes the code/state on load
            and AuthCallback sends the user back to "/", so this is just a spinner. */}
        <Route path="/callback" element={<AuthCallback />} />
        {/* Hidden iframe target for silent token renewal. */}
        <Route path="/silent-renew" element={<div />} />

        {/* All other pages use the main layout */}
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
              <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="flex justify-between h-16">
                    <div className="flex items-center">
                      <Link to="/" className="flex items-center space-x-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                        <span className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">RecipeVault</span>
                      </Link>
                    </div>
                    <div className="hidden md:flex items-center space-x-8">
                      <Link to="/" className="text-gray-600 hover:text-orange-600 font-medium transition-colors">Home</Link>
                      <Link to="/recipes" className="text-gray-600 hover:text-orange-600 font-medium transition-colors">Recipes</Link>
                      <Link to="/about" className="text-gray-600 hover:text-orange-600 font-medium transition-colors">About</Link>
                      <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-orange-500/25 transition-all">
                        Add Recipe
                      </button>
                      <AuthButton />
                    </div>
                    <div className="md:hidden flex items-center">
                      <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-600 p-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                {mobileMenuOpen && (
                  <div className="md:hidden bg-white border-t">
                    <div className="px-4 py-3 space-y-3">
                      <Link to="/" className="block text-gray-600 hover:text-orange-600 font-medium">Home</Link>
                      <Link to="/recipes" className="block text-gray-600 hover:text-orange-600 font-medium">Recipes</Link>
                      <Link to="/about" className="block text-gray-600 hover:text-orange-600 font-medium">About</Link>
                    </div>
                  </div>
                )}
              </nav>

              <main>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/recipes" element={<Recipes />} />
                  <Route path="/about" element={<About />} />
                </Routes>
              </main>

              <footer className="bg-gray-900 text-gray-400 py-8 mt-20">
                <div className="max-w-7xl mx-auto px-4 text-center">
                  <p>&copy; 2025 RecipeVault. All rights reserved.</p>
                </div>
              </footer>
            </div>
          }
        />
      </Routes>
    </Router>
  )
}

function Home() {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Photo Upload',
      description: 'Capture beautiful photos of your dishes and store them with your recipes.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Smart Conversion',
      description: 'Automatically convert measurements between metric and imperial units.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
      title: 'Tags & Categories',
      description: 'Organize recipes with custom tags and categories for easy searching.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      title: 'Quick Search',
      description: 'Find any recipe instantly with powerful full-text search.'
    }
  ]

  return (
    <>
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-20 sm:py-32">
        <div className="text-center">
          <h1 className="text-5xl sm:text-7xl font-bold text-gray-900 mb-6">
            Your Digital
            <span className="block bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              Recipe Collection
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            Store, organize, and discover your favorite recipes all in one place.
            Never lose a family recipe again.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-orange-500/25 transition-all transform hover:-translate-y-1">
              Get Started Free
            </button>
            <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg hover:border-orange-500 hover:text-orange-600 transition-all">
              Browse Recipes
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Everything you need</h2>
          <p className="text-xl text-gray-600">Powerful features to manage your culinary creations</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg shadow-gray-200/50 hover:shadow-xl hover:shadow-orange-500/10 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl flex items-center justify-center text-orange-600 mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-3xl p-12 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to start cooking?</h2>
          <p className="text-xl opacity-90 mb-8">Join thousands of home cooks organizing their recipes with RecipeVault.</p>
          <button className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
            Create Your Vault
          </button>
        </div>
      </section>
    </>
  )
}

function Recipes() {
  return (
    <section className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Your Recipes</h1>
        <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-orange-500/25 transition-all">
          + Add Recipe
        </button>
      </div>
      <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-12 text-center">
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">No recipes yet</h2>
        <p className="text-gray-600 mb-6">Start building your collection by adding your first recipe.</p>
        <button className="text-orange-600 font-medium hover:text-orange-700">Add your first recipe →</button>
      </div>
    </section>
  )
}

function About() {
  return (
    <section className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">About RecipeVault</h1>
      <div className="prose prose-lg text-gray-600">
        <p className="text-xl mb-6">
          RecipeVault is your personal digital cookbook, designed to help you organize
          and share your favorite recipes with ease.
        </p>
        <p className="mb-6">
          Whether you're preserving cherished family recipes passed down through generations
          or collecting new culinary discoveries, RecipeVault provides a beautiful, organized
          space for all your cooking inspirations.
        </p>
        <div className="bg-orange-50 rounded-2xl p-6 border border-orange-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Key Features</h3>
          <ul className="space-y-2 text-gray-700">
            <li>• Upload and store recipe photos</li>
            <li>• Automatic measurement conversion</li>
            <li>• Tag and categorize recipes</li>
            <li>• Powerful search functionality</li>
            <li>• Export and share recipes easily</li>
          </ul>
        </div>
      </div>
    </section>
  )
}

export default App
